import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const CustomModuleBuilder = ({ isOpen, onClose, onSave, editingModule = null }) => {
  const { currentUser } = useAuth();
  
  const [moduleName, setModuleName] = useState('');
  const [itemText, setItemText] = useState('');
  const [rows, setRows] = useState(2);
  const [columns, setColumns] = useState(2);
  const [cells, setCells] = useState([
    [{ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false, indent: 0 }, { text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false, indent: 0 }],
    [{ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false, indent: 0 }, { text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false, indent: 0 }]
  ]);
  const [savedModules, setSavedModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('create'); // 'create' or 'library'
  const [useCheckboxScoring, setUseCheckboxScoring] = useState(false); // Control scoring mode

  // Load saved modules
  useEffect(() => {
    if (isOpen) {
      loadSavedModules();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load module data if editing
  useEffect(() => {
    if (editingModule) {
      setModuleName(editingModule.moduleName || '');
      setItemText(editingModule.itemText || '');
      setUseCheckboxScoring(editingModule.useCheckboxScoring || false);
      if (editingModule.cells) {
        setCells(editingModule.cells);
        setRows(editingModule.cells.length);
        setColumns(editingModule.cells[0]?.length || 2);
      } else {
        // Convert old format if needed
        setRows(2);
        setColumns(2);
        setCells([
          [{ text: '', type: 'checkbox' }, { text: '', type: 'checkbox' }],
          [{ text: '', type: 'checkbox' }, { text: '', type: 'checkbox' }]
        ]);
      }
      setActiveTab('create');
    } else {
      resetForm();
    }
  }, [editingModule]);

  const resetForm = () => {
    setModuleName('');
    setItemText('');
    setRows(2);
    setColumns(2);
    setUseCheckboxScoring(false);
    setCells([
      [{ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false }, { text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false }],
      [{ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false }, { text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false }]
    ]);
  };

  const loadSavedModules = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'customModules'));
      const modules = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Reconstruct cells if they were flattened
          cells: data.cells && data.rows && data.columns 
            ? reconstructCellsFromFirestore(data.cells, data.rows, data.columns)
            : data.cells || [[{ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false }]]
        };
      });
      setSavedModules(modules);
    } catch (error) {
      console.error('Error loading saved modules:', error);
    }
  };

  const updateTableSize = (newRows, newColumns) => {
    const currentCells = [...cells];
    
    // Adjust rows
    while (currentCells.length < newRows) {
      const newRow = Array(newColumns).fill().map(() => ({ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false }));
      currentCells.push(newRow);
    }
    while (currentCells.length > newRows) {
      currentCells.pop();
    }
    
    // Adjust columns in each row
    currentCells.forEach(row => {
      while (row.length < newColumns) {
        row.push({ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false });
      }
      while (row.length > newColumns) {
        row.pop();
      }
    });
    
    setCells(currentCells);
  };

  const updateCell = (rowIndex, colIndex, field, value) => {
    const newCells = [...cells];
    newCells[rowIndex][colIndex] = { ...newCells[rowIndex][colIndex], [field]: value };
    setCells(newCells);
  };

  const getTotalCheckboxes = () => {
    return cells.flat().filter(cell => cell.type === 'checkbox').length;
  };

  // Helper functions to handle Firestore's limitation with nested arrays
  const flattenCellsForFirestore = (cells2D) => {
    return cells2D.flat(); // Flatten 2D array to 1D
  };

  const reconstructCellsFromFirestore = (flatCells, rows, cols) => {
    const cells2D = [];
    for (let i = 0; i < rows; i++) {
      const rowCells = [];
      for (let j = 0; j < cols; j++) {
        const index = i * cols + j;
        const cell = flatCells[index] || { text: '', type: 'checkbox' };
        // Ensure all properties exist with defaults
        rowCells.push({
          text: cell.text || '',
          type: cell.type || 'checkbox',
          bold: cell.bold || false,
          italic: cell.italic || false,
          checkboxPosition: cell.checkboxPosition || 'bottom',
          editable: cell.editable || false
        });
      }
      cells2D.push(rowCells);
    }
    return cells2D;
  };

  const saveModule = async () => {
    if (!moduleName.trim() || !itemText.trim()) {
      alert('Please fill in module name and item text');
      return;
    }

    if (cells.flat().some(cell => !cell.text.trim())) {
      alert('Please fill in all cell texts');
      return;
    }

    setLoading(true);
    try {
      const moduleData = {
        moduleName: moduleName.trim(),
        itemText: itemText.trim(),
        cells: flattenCellsForFirestore(cells), // Flatten cells before saving
        rows: rows,
        columns: columns,
        useCheckboxScoring: useCheckboxScoring,
        checkboxCount: getTotalCheckboxes(),
        createdBy: currentUser?.uid,
        createdAt: serverTimestamp()
      };

      await addDoc(collection(db, 'customModules'), moduleData);
      await loadSavedModules();
      alert('Module saved successfully!');
      resetForm();
    } catch (error) {
      console.error('Error saving module:', error);
      alert('Failed to save module');
    }
    setLoading(false);
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('Are you sure you want to delete this module?')) return;

    try {
      await deleteDoc(doc(db, 'customModules', moduleId));
      await loadSavedModules();
    } catch (error) {
      console.error('Error deleting module:', error);
      alert('Failed to delete module');
    }
  };

  const addModuleToAssessment = (module) => {
    // Ensure cells are properly reconstructed if needed
    const cells = module.cells && module.rows && module.columns && Array.isArray(module.cells) && !Array.isArray(module.cells[0])
      ? reconstructCellsFromFirestore(module.cells, module.rows, module.columns)
      : module.cells || [[{ text: '', type: 'checkbox', bold: false, italic: false, textAlign: 'center', checkboxPosition: 'bottom', editable: false }]];
      
    const customRow = {
      id: Date.now(),
      type: 'custom',
      moduleName: module.moduleName,
      itemText: module.itemText,
      cells: cells,
      rows: module.rows || 1,
      columns: module.columns || 1,
      useCheckboxScoring: module.useCheckboxScoring || false,
      critical: false,
      scoreAwarded: 0,
      pointsPossible: module.useCheckboxScoring ? cells.flat().filter(cell => cell.type === 'checkbox').length : 1
    };

    onSave(customRow);
    onClose();
  };

  const createCustomRow = () => {
    if (!itemText.trim()) {
      alert('Please fill in the item text');
      return;
    }

    if (cells.flat().some(cell => !cell.text.trim())) {
      alert('Please fill in all cell texts');
      return;
    }

    const customRow = {
      ...(editingModule || {}), // Preserve existing properties when editing
      id: editingModule?.id || Date.now(),
      type: 'custom',
      moduleName: moduleName.trim() || 'Custom Module',
      itemText: itemText.trim(),
      cells: cells,
      rows: rows,
      columns: columns,
      useCheckboxScoring: useCheckboxScoring,
      pointsPossible: useCheckboxScoring ? getTotalCheckboxes() : 1,
      // Only reset these if it's a new module
      ...(editingModule ? {} : {
        critical: false,
        scoreAwarded: 0
      })
    };

    onSave(customRow);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">

{/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {editingModule ? 'Edit Custom Module' : 'Custom Module Builder'}
              </h2>
              <p className="text-sm text-gray-600">
                Design custom assessment tables with checkboxes and text
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors"
            title="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

{/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-6 py-4 font-medium text-sm transition-all flex items-center gap-2 ${
              activeTab === 'create'
                ? 'border-b-2 border-blue-600 text-blue-600 bg-white -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Create Module
          </button>
          <button
            onClick={() => setActiveTab('library')}
            className={`px-6 py-4 font-medium text-sm transition-all flex items-center gap-2 ${
              activeTab === 'library'
                ? 'border-b-2 border-blue-600 text-blue-600 bg-white -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Module Library
          </button>
        </div>

{/* Create Tab */}
        {activeTab === 'create' && (
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-4 bg-gray-200">

{/* Module Info */}
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 shadow-sm shadow-gray-500">
                <h3 className="text-lg font-semibold text-blue-600 mb-2 flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Module Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Module Name <span className="text-gray-500">(for saving)</span>
                    </label>
                    <input
                      type="text"
                      value={moduleName}
                      onChange={(e) => setModuleName(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="e.g., Mental Status Assessment"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item Text
                      <span className="text-xs text-blue-600 ml-2 bg-blue-50 px-2 py-1 rounded">(Shift+Enter for new line)</span>
                    </label>
                    <textarea
                      value={itemText}
                      onChange={(e) => setItemText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault(); // Prevent form submission on Enter
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center resize-vertical min-h-[60px] transition-colors"
                      placeholder="e.g., Mental Status&#10;Assessment"
                      required
                      rows="2"
                    />
                  </div>
                </div>
              </div>
                
{/* Table Designer */}
              <div className="bg-white p-3 rounded-xl shadow-sm shadow-gray-500">
                <h3 className="text-xl font-semibold text-white mb-2 flex items-center gap-2 bg-blue-500 rounded-lg px-3 py-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Table Designer
                </h3>

{/* Table Size Controls */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rows
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={rows}
                      onChange={(e) => {
                        const newRows = parseInt(e.target.value) || 1;
                        setRows(newRows);
                        updateTableSize(newRows, columns);
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Columns
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={columns}
                      onChange={(e) => {
                        const newColumns = parseInt(e.target.value) || 1;
                        setColumns(newColumns);
                        updateTableSize(rows, newColumns);
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>

{/* Scoring Control */}
                <div className="ml-4 flex items-center text-center align-center py-2 mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={useCheckboxScoring}
                      onChange={(e) => setUseCheckboxScoring(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Use checkbox-based scoring ({getTotalCheckboxes()} points)
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    {useCheckboxScoring 
                      ? "Each checkbox counts as 1 point" 
                      : "Entire module counts as 1 point regardless of checkboxes"
                    }
                  </p>
                </div>
                <h3 className="text-xl font-semibold text-white bg-blue-500 rounded-lg mb-2 flex items-center gap-2 px-3 py-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview
                </h3>
              <div className="bg-white overflow-hidden">
                              <table className="w-full">
                                <thead>
                                  <tr className="bg-white">
                                    <th className="text-left p-2 w-1/4">Item</th>
                                    <th className="text-left p-2">Objectives ({rows}×{columns} Table)</th>
                                    <th className="text-center p-2 w-16">Points</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td className="bg-white p-2 align-middle text-center font-medium">
                                      <div className="whitespace-pre-line leading-tight">
                                        {itemText || 'Item Text'}
                                      </div>
                                    </td>
                                    <td className="bg-white p-2">
                                      <table className="w-full">
                                        <tbody>
                                          {cells.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                              {row.map((cell, colIndex) => (
                                                <td key={colIndex} className="border border-purple-200 p-2 text-center text-sm bg-purple-50">
                                                  {cell.type === 'checkbox' ? (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                      <div className={`flex items-center justify-center ${
                                                        cell.checkboxPosition === 'left' 
                                                          ? 'flex-row gap-2 w-full' 
                                                          : cell.checkboxPosition === 'right'
                                                          ? 'flex-row-reverse gap-2 w-full'
                                                          : 'flex-col gap-2 w-full h-full'
                                                      }`}>
                                                        {(cell.checkboxPosition === 'left' || cell.checkboxPosition === 'right') && (
                                                          <div className="w-4 h-4 flex items-center justify-center">
                                                            <div className="w-4 h-4 border border-gray-400 rounded"></div>
                                                          </div>
                                                        )}
                                                        <div className="flex-1 flex items-center justify-center min-w-0">
                                                          {cell.editable ? (
                                                            <div className={`bg-white border border-gray-200 rounded px-2 py-1 text-${cell.textAlign || 'center'} break-words w-full ${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>
                                                              {cell.text || `Cell ${rowIndex + 1}-${colIndex + 1}`}
                                                            </div>
                                                          ) : (
                                                            <div className={`text-${cell.textAlign || 'center'} break-words w-full px-1 ${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>
                                                              {cell.text || `Cell ${rowIndex + 1}-${colIndex + 1}`}
                                                            </div>
                                                          )}
                                                        </div>
                                                        {cell.checkboxPosition === 'bottom' && (
                                                          <div className="w-4 h-4 flex items-center justify-center">
                                                            <div className="w-4 h-4 border border-gray-400 rounded"></div>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <div className="w-full h-full flex items-center justify-center min-w-0">
                                                      {cell.editable ? (
                                                        <div className={`bg-white border border-gray-200 rounded px-2 py-1 text-${cell.textAlign || 'center'} break-words w-full ${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>
                                                          {cell.text || `Cell ${rowIndex + 1}-${colIndex + 1}`}
                                                        </div>
                                                      ) : (
                                                        <div className={`text-${cell.textAlign || 'center'} break-words w-full px-1 ${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>
                                                          {cell.text || `Cell ${rowIndex + 1}-${colIndex + 1}`}
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </td>
                                    <td className="p-3 text-center align-top">
                                      <div className="font-bold text-lg">
                                        {useCheckboxScoring ? getTotalCheckboxes() : 1}
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

{/* Table Editor */}
                <div className="bg-white overflow-hidden mb-3 shadow-gray-500 shadow-sm p-2">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <tbody>
                        {cells.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((cell, colIndex) => (
                              <td key={colIndex} className="border-none p-1 min-w-24">
                                <div className="space-y-1">
                                  <input
                                    type="text"
                                    value={cell.text}
                                    onChange={(e) => updateCell(rowIndex, colIndex, 'text', e.target.value)}
                                    className={`w-full border bg-yellow-50 border-amber-500 rounded px-3 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                                      cell.bold ? 'font-bold' : ''
                                    } ${cell.italic ? 'italic' : ''}`}
                                    placeholder={`Cell ${rowIndex + 1}-${colIndex + 1}`}
                                  />
                                  
{/* Responsive Toolbar */}
                                  <div className="space-y-1">

 {/* Text Controls */}
                                    <div className="border border-gray-200 rounded p-1">
                                      <div className="text-xs font-semibold text-blue-600 mb-1 text-center bg-blue-50 rounded px-2 py-1">Text</div>
                                      <div className="space-y-1">

{/* Formatting Row */}
                                        <div className="flex gap-0.5 justify-center">
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'bold', !cell.bold)}
                                            className={`w-6 h-6 flex items-center justify-center text-xs font-bold border rounded transition-colors ${
                                              cell.bold 
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Bold"
                                          >
                                            B
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'italic', !cell.italic)}
                                            className={`w-6 h-6 flex items-center justify-center text-xs italic border rounded transition-colors ${
                                              cell.italic 
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Italic"
                                          >
                                            I
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'editable', !cell.editable)}
                                            className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                              cell.editable 
                                                ? 'bg-green-500 text-white border-green-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Editable in Builder"
                                          >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                            </svg>
                                          </button>
                                        </div>
{/* Alignment Row */}
                                        <div className="flex gap-0.5 justify-center">
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'textAlign', 'left')}
                                            className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                              (cell.textAlign || 'center') === 'left'
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Align Left"
                                          >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path d="M2 4a1 1 0 011-1h8a1 1 0 110 2H3a1 1 0 01-1-1zM2 8a1 1 0 011-1h12a1 1 0 110 2H3a1 1 0 01-1-1zM2 12a1 1 0 011-1h6a1 1 0 110 2H3a1 1 0 01-1-1zM2 16a1 1 0 011-1h10a1 1 0 110 2H3a1 1 0 01-1-1z"/>
                                            </svg>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'textAlign', 'center')}
                                            className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                              (cell.textAlign || 'center') === 'center'
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Align Center"
                                          >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path d="M4 4a1 1 0 011-1h6a1 1 0 110 2H5a1 1 0 01-1-1zM2 8a1 1 0 011-1h12a1 1 0 110 2H3a1 1 0 01-1-1zM5 12a1 1 0 011-1h4a1 1 0 110 2H6a1 1 0 01-1-1zM3 16a1 1 0 011-1h8a1 1 0 110 2H4a1 1 0 01-1-1z"/>
                                            </svg>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'textAlign', 'right')}
                                            className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                              (cell.textAlign || 'center') === 'right'
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Align Right"
                                          >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path d="M11 4a1 1 0 011-1h4a1 1 0 110 2h-4a1 1 0 01-1-1zM2 8a1 1 0 011-1h12a1 1 0 110 2H3a1 1 0 01-1-1zM9 12a1 1 0 011-1h6a1 1 0 110 2h-6a1 1 0 01-1-1zM5 16a1 1 0 011-1h10a1 1 0 110 2H6a1 1 0 01-1-1z"/>
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    </div>

{/* Type Controls */}
                                    <div className="border border-gray-200 rounded p-1">
                                      <div className="text-xs font-semibold text-blue-600 mb-1 text-center bg-blue-50 rounded px-2 py-1">Type</div>
                                      <div className="flex gap-0.5 justify-center">
                                        <button
                                          type="button"
                                          onClick={() => updateCell(rowIndex, colIndex, 'type', 'text')}
                                          className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                            cell.type === 'text'
                                              ? 'bg-blue-500 text-white border-blue-500' 
                                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                          }`}
                                          title="Text Cell"
                                        >
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2.5 4A1.5 1.5 0 001 5.5v9A1.5 1.5 0 002.5 16h15a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0017.5 4h-15zM2 5.5A.5.5 0 012.5 5h15a.5.5 0 01.5.5v1H2v-1zM18 8v6.5a.5.5 0 01-.5.5h-15a.5.5 0 01-.5-.5V8h16zM5 10.25a.75.75 0 01.75-.75h8.5a.75.75 0 010 1.5h-8.5a.75.75 0 01-.75-.75zm0 2.5a.75.75 0 01.75-.75h4.5a.75.75 0 010 1.5h-4.5a.75.75 0 01-.75-.75z"/>
                                          </svg>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => updateCell(rowIndex, colIndex, 'type', 'checkbox')}
                                          className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                            cell.type === 'checkbox'
                                              ? 'bg-blue-500 text-white border-blue-500' 
                                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                          }`}
                                          title="Checkbox Cell"
                                        >
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>

{/* Checkbox Controls - Only when checkbox type */}
                                    {cell.type === 'checkbox' && (
                                      <div className="border border-gray-200 rounded p-1">
                                        <div className="text-xs font-semibold text-blue-600 mb-1 text-center bg-blue-50 rounded px-2 py-1">Checkbox</div>
                                        <div className="flex gap-0.5 justify-center">
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'checkboxPosition', 'left')}
                                            className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                              cell.checkboxPosition === 'left'
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Checkbox Left"
                                          >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                            </svg>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'checkboxPosition', 'bottom')}
                                            className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                              (cell.checkboxPosition || 'bottom') === 'bottom'
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Checkbox Bottom"
                                          >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => updateCell(rowIndex, colIndex, 'checkboxPosition', 'right')}
                                            className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                                              cell.checkboxPosition === 'right'
                                                ? 'bg-blue-500 text-white border-blue-500' 
                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                            title="Checkbox Right"
                                          >
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Points:</strong> {useCheckboxScoring ? `${getTotalCheckboxes()} points (1 per checkbox)` : "1 point (fixed)"}
                  </p>
                </div>
                </div>


           

{/* Actions */}
                <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 mt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {moduleName.trim() && (
                        <button
                          onClick={saveModule}
                          disabled={loading}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              Save to Library
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-all duration-200 flex items-center gap-2"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Cancel
                      </button>
                      <button
                        onClick={createCustomRow}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-sm"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {editingModule ? 'Update Row' : 'Add to Assessment'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

{/* Library Tab */}
            {activeTab === 'library' && (
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    Saved Modules
                  </h3>
                  <p className="text-sm text-gray-600">Click on a module to add it to your assessment</p>
                </div>

                {savedModules.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No saved modules yet</h3>
                    <p className="text-gray-500 mb-4">Create and save a module to see it here.</p>
                    <button
                      onClick={() => setActiveTab('create')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Create Your First Module
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {savedModules.map((module) => (
                      <div
                        key={module.id}
                        className="bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-md transition-all duration-200 cursor-pointer group"
                        onClick={() => addModuleToAssessment(module)}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <h4 className="font-semibold text-gray-900 text-lg group-hover:text-blue-600 transition-colors">{module.moduleName}</h4>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteModule(module.id);
                            }}
                            className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-all duration-200"
                            title="Delete module"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm font-medium text-gray-700 mb-1">Item Text:</p>
                            <p className="text-sm text-gray-600 whitespace-pre-line leading-snug">{module.itemText}</p>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              <strong>Table Size:</strong> {module.rows || 1}×{module.columns || 1}
                            </span>
                            <span className="text-gray-600">
                              <strong>Scoring:</strong> {module.useCheckboxScoring ? `${module.checkboxCount || 0} pts` : "1 pt"}
                            </span>
                          </div>
                          <div className="mt-4 text-center">
                            <div className="inline-flex items-center text-sm text-blue-600 font-medium group-hover:text-blue-700">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Click to add to assessment
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    };

    export default CustomModuleBuilder;
